/**
 * Theme — light / dark / system, per-user.
 *
 * Storage:
 *   - localStorage["theme_preference"] is the cache. Read by the inline
 *     <head> script in index.html so the right tokens load on first paint
 *     (no FOUC).
 *   - profiles.theme_preference is the source of truth across devices. We
 *     sync it into localStorage after login.
 *
 * Resolution:
 *   - Preference is one of "system" | "light" | "dark".
 *   - "system" resolves against window.matchMedia('(prefers-color-scheme: dark)').
 *   - The resolved value is set on document.documentElement.dataset.theme
 *     ("light" | "dark"), which is the only DOM hook our CSS reads.
 *
 * The OS preference is observed via matchMedia change events, so a user on
 * "system" who toggles macOS dark mode flips the portal automatically.
 */

const STORAGE_KEY = "theme_preference";
const VALID_PREFERENCES = new Set(["system", "light", "dark"]);

let _systemMql = null;
let _systemListenerAttached = false;
let _supabaseRef = null;
let _profileIdRef = null;

/* ─── Read / write preference ─────────────────────────────────── */

export function getStoredPreference() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID_PREFERENCES.has(v) ? v : "system";
  } catch {
    return "system";
  }
}

function writeStoredPreference(pref) {
  try {
    if (VALID_PREFERENCES.has(pref)) localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // localStorage might be disabled (private mode, etc.). Silent fail —
    // the in-memory state still drives the current session.
  }
}

/* ─── Resolve to a concrete theme + apply ─────────────────────── */

function resolveSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolvePreference(pref) {
  if (pref === "light" || pref === "dark") return pref;
  return resolveSystemTheme();
}

export function applyResolvedTheme(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (root.dataset.theme !== resolved) {
    root.dataset.theme = resolved;
  }
}

/* ─── Public API ──────────────────────────────────────────────── */

/**
 * Initialise theme on boot. Idempotent — safe to call multiple times.
 * Supabase + profileId are optional; pass them after sign-in to enable
 * DB-side persistence and cross-device sync.
 */
export function initTheme({ supabase = null, profileId = null, dbPreference = null } = {}) {
  _supabaseRef = supabase;
  _profileIdRef = profileId;

  // If the DB row is fresher than what we have locally (e.g. user changed
  // the theme on another device), trust the DB and update localStorage.
  if (dbPreference && VALID_PREFERENCES.has(dbPreference)) {
    if (dbPreference !== getStoredPreference()) {
      writeStoredPreference(dbPreference);
    }
  }

  const pref = getStoredPreference();
  applyResolvedTheme(resolvePreference(pref));

  attachSystemListener();
}

/**
 * Update preference in-memory + localStorage + DB (if signed in).
 * Returns the resolved theme that was applied.
 */
export async function setThemePreference(pref) {
  if (!VALID_PREFERENCES.has(pref)) {
    throw new Error(`Invalid theme preference: ${pref}`);
  }

  writeStoredPreference(pref);
  const resolved = resolvePreference(pref);
  applyResolvedTheme(resolved);

  // Best-effort DB write. Failures don't break the in-memory + localStorage
  // state, so the change is still effective for this session.
  if (_supabaseRef && _profileIdRef) {
    try {
      const { error } = await _supabaseRef
        .from("profiles")
        .update({ theme_preference: pref })
        .eq("id", _profileIdRef);
      if (error) console.warn("setThemePreference DB write failed:", error);
    } catch (err) {
      console.warn("setThemePreference DB write threw:", err);
    }
  }

  return resolved;
}

/* ─── OS preference change listener ───────────────────────────── */

function attachSystemListener() {
  if (_systemListenerAttached) return;
  if (typeof window === "undefined" || !window.matchMedia) return;

  _systemMql = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    // Only react if the user is on "system" — explicit light/dark wins.
    if (getStoredPreference() === "system") {
      applyResolvedTheme(resolvePreference("system"));
    }
  };

  // Modern Safari + Chrome use addEventListener; older Safari needs
  // addListener. Try the modern API first, fall back gracefully.
  if (_systemMql.addEventListener) _systemMql.addEventListener("change", handler);
  else if (_systemMql.addListener) _systemMql.addListener(handler);

  _systemListenerAttached = true;
}
